import React from 'react';
import PropTypes from 'prop-types';
import Message from './Message';
import '../chat-css/MessageList.css';
import {convertFormBackendDate, sameDay} from '../../../../../utils/General';
import Translate from "../../../../../components/Translate";
import TypingIndicator from "./TypingIndicator";
import {emitter} from "../../../../../services/emitter";
import ForumIcon from "../../../../../assets/icons/ForumIcon";
import ChevronDownIcon from "../../../../../assets/icons/ChevronDownIcon";
import LoadingComponent from "../../../../../components-new/ui/LoadingComponent";


/*
 * How long the view takes to walk down to a message that has just arrived. The
 * same stretch the typing row takes to shut - see TypingIndicator/styles.css -
 * so the row leaving and the view following it are one movement and not two.
 */
const SCROLL_MS = 300;

class MessageList extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            sentMessages: [],
            newMessages: [],
            isAtBottom: true,
            newMessageCount: 0,
            lastMessageDate: null,
            activeTab: 'PHOTO',
            showMediaViewDiv: false,
        };
        this.bottomDiv = React.createRef();
        this.messageWindow = React.createRef();
        this.dateRefs = {};

    }

    componentDidMount() {
        const {messages} = this.props;
        if (messages.length > 0) {
            const lastMessage = messages[messages.length - 1];
            this.setState({lastMessageDate: lastMessage.date});
        }

        /*
         * At the foot, with nothing to watch. scrollIntoView moves every
         * scrollable ancestor it can find, the page behind the panel included,
         * and that is the lurch a chat used to open with.
         */
        this.pinToBottom();

        /*
         * The height of the window itself, which changes without anybody
         * scrolling: the composer grows by a line, the reply quote opens, the
         * takeover bar goes. Whatever the reason, a view that was at the foot
         * should still be at the foot afterwards.
         */
        if (typeof ResizeObserver !== 'undefined' && this.messageWindow.current) {
            this.sizeWatcher = new ResizeObserver(() => {
                if (this.state.isAtBottom) this.pinToBottom();
            });
            this.sizeWatcher.observe(this.messageWindow.current);
        }

        emitter.on('chatScrollToBottom', this.scrollToBottom, 'messageListSubscription');
        emitter.on('chatScrollToSearchResult', this.scrollToSearchResult, 'messageListSubscription');
        emitter.on('scrollToMessageById', this.scrollToMessageById, 'messageListSubscription');
    }

    componentWillUnmount() {
        cancelAnimationFrame(this.scrollFrame);
        if (this.sizeWatcher) this.sizeWatcher.disconnect();
        emitter.unSubscribe('chatScrollToBottom', 'messageListSubscription');
        emitter.unSubscribe('chatScrollToSearchResult', 'messageListSubscription');
        emitter.unSubscribe('scrollToMessageById', 'messageListSubscription');
    }

    /*
     * How far the foot was from the view, measured before the browser lays the
     * update out.
     *
     * Older messages are added to the end of the array and drawn above
     * everything else, so what the operator is reading is pushed down by the
     * height of the whole batch. Restoring that distance afterwards leaves it
     * exactly where it was, and the history grows upwards, out of sight.
     */
    getSnapshotBeforeUpdate(prevProps) {
        const container = this.messageWindow.current;
        if (!container) return null;

        const previous = prevProps.messages;
        const current = this.props.messages;

        const grewAtTheTop = current.length > previous.length
            && previous.length > 0
            && previous[0] && current[0]
            && previous[0].id === current[0].id;

        return grewAtTheTop ? container.scrollHeight - container.scrollTop : null;
    }

    componentDidUpdate(prevProps, prevState, distanceToFoot) {
        const {messages, messageConfig} = this.props;
        const {messages: oldMessages} = prevProps;

        if (distanceToFoot != null) {
            const container = this.messageWindow.current;
            if (container) container.scrollTop = container.scrollHeight - distanceToFoot;
        }

        /*
         * What the operator is allowed to do arrives from the server a moment
         * after the chat has been drawn, and it changes the height of every
         * message in it. Permission to react puts a two-pixel strip under each
         * one - see the end of the bubble in Message.jsx - and permission to see
         * drafts can add a whole message. Two pixels is nothing; two pixels on
         * two hundred messages is four hundred, and the last several fall off
         * the bottom of a conversation that was scrolled to the end correctly a
         * moment earlier.
         *
         * The scroll was never wrong. What changed was the height underneath it,
         * and nothing was watching for that.
         *
         * It shows on some chats and not others because the answer differs: the
         * strip needs webchat, reactions switched on for the company and in its
         * tariff, and a chat the operator is not merely supervising. Where any
         * of that is missing the conversation does not grow at all.
         *
         * Only while the foot is what is being read. An operator who has
         * scrolled up into the history has not asked to be taken anywhere.
         */
        if (prevProps.messageConfig !== messageConfig && this.state.isAtBottom) {
            this.pinToBottom();
        }

        const prevLastMessage = oldMessages[oldMessages.length - 1];
        const currentLastMessage = messages[messages.length - 1];

        if (oldMessages.length !== messages.length) {
            const newSentMessages = messages.filter(
                msg => msg.sender === "self" && !oldMessages.some(oldMsg => oldMsg.id === msg.id)
            );

            /*
             * A draft is never one of these.
             *
             * It is keyed by its own text, so every keystroke is a new id and
             * looks from here like something that has just arrived - which
             * would fade it up where it stands and count it towards the unread
             * number on the button. Neither is true: nothing has been said
             * yet, and the same half-sentence is simply being redrawn.
             */
            const newReceivedMessages = messages.filter(msg => {
                const isNew = msg.sender !== "self" &&
                    !msg.draftMessage &&
                    !oldMessages.some(oldMsg => oldMsg.id === msg.id) &&
                    (!this.state.lastMessageDate || convertFormBackendDate(msg.date) > convertFormBackendDate(this.state.lastMessageDate));
                return isNew;
            });

            this.setState(prevState => ({
                sentMessages: [...prevState.sentMessages, ...newSentMessages],
                newMessages: [...prevState.newMessages, ...newReceivedMessages],
                newMessageCount: prevState.isAtBottom ? 0 : prevState.newMessageCount + newReceivedMessages.length,
                lastMessageDate: this.state.isAtBottom && currentLastMessage ? currentLastMessage.date : prevState.lastMessageDate,
            }), () => {
                if (this.state.isAtBottom) {
                    this.followToBottom();
                }
            });
        }

        if (oldMessages.length > 0 && messages.length > 0 && oldMessages[oldMessages.length - 1].id !== messages[messages.length - 1].id) {
            if (this.state.isAtBottom) {
                this.followToBottom();
            }
        }

        /*
         * The first fill of a chat: the foot, at once and without movement.
         *
         * A message arriving used to be caught here as well, a line after the
         * walk down had already been started - and an instant jump in the
         * middle of that walk is what it was fighting with.
         */
        if (oldMessages.length === 0 && messages.length > 0 && this.state.isAtBottom) {
            this.pinToBottom();
        }

    }

    handleScroll = () => {
        const {messagesTotalNumber, messages, loadingOlderMessages, loadOlderMessages} = this.props;

        const container = this.messageWindow.current;
        if (!container) return;

        if (messagesTotalNumber > messages.length && !loadingOlderMessages) {
            if (container.scrollTop < 25) {
                loadOlderMessages();
            }
        }

        /* our own walk down, mid-step: the view is on its way to the foot and
           is to be read as being there */
        if (this.autoScrolling) return;

        /*
         * Forty pixels, not ten. Ten is tighter than the things that move the
         * foot about by themselves - a line appearing in the composer, an
         * image settling into its bubble - and the button was being shown for
         * a drift no operator had asked for.
         */
        const distance = container.scrollHeight - container.scrollTop - container.clientHeight;
        const isAtBottom = Math.abs(distance) < 40;

        /* a scroll raises these by the dozen; only a change is worth a render */
        if (isAtBottom !== this.state.isAtBottom) {
            this.setState({isAtBottom});
        }

        if (isAtBottom && this.state.newMessageCount > 0) {
            this.scrollToBottom();
        }
    };

    /*
     * Puts the view at the foot, with no movement to watch.
     *
     * For staying somewhere rather than travelling there: the conversation has
     * been laid out again under a view that was already at the end, and the end
     * is simply somewhere else now. Nothing arrived, so there is nothing to
     * follow and nothing to animate.
     *
     * Set on the container rather than through scrollIntoView, which also moves
     * every scrollable ancestor - the page behind the panel included.
     */
    pinToBottom = () => {
        cancelAnimationFrame(this.scrollFrame);

        const container = this.messageWindow.current;
        if (!container) return;

        container.scrollTop = container.scrollHeight;
        this.autoScrolling = false;
    };

    /*
     * Walks the view down to the foot of the conversation.
     *
     * Two things used to happen at once when a message arrived, and they did not
     * agree. One set scrollTop to how much the conversation had grown by, which
     * is not a position at all - on a long chat it threw the view near the top.
     * The other was a native smooth scrollIntoView, which then had to crawl all
     * the way back down from there: that crawl is the lag.
     *
     * One scroll now, and it reads the foot again on every frame rather than
     * aiming at where the foot was when it started. That matters here because
     * the foot is moving while this runs - the typing row is shutting underneath
     * as the message that replaces it opens above, and a target taken up front
     * would be a target that no longer exists by the time it is reached.
     */
    followToBottom = () => {
        cancelAnimationFrame(this.scrollFrame);

        const container = this.messageWindow.current;
        if (!container) return;

        const from = container.scrollTop;
        const start = performance.now();

        this.autoScrolling = true;

        const step = now => {
            const progress = Math.min(1, (now - start) / SCROLL_MS);
            const target = container.scrollHeight - container.clientHeight;
            const ease = progress < 0.5
                ? 4 * progress * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 3) / 2;

            container.scrollTop = from + (target - from) * ease;

            if (progress < 1) {
                this.scrollFrame = requestAnimationFrame(step);
            } else {
                this.autoScrolling = false;
            }
        };

        this.scrollFrame = requestAnimationFrame(step);
    };

    scrollToBottom = () => {
        this.followToBottom();
        this.setState({newMessageCount: 0, isAtBottom: true});
    };

    scrollToSearchResult = (resultIndex) => {
        const result = this.props.searchResults[resultIndex];
        if (result && this.dateRefs[result.id] && this.dateRefs[result.id].current) {
            this.dateRefs[result.id].current.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    };

    scrollToMessageById = (id) => {
        const ref = this.dateRefs[id];
        if (ref && ref.current) {
            ref.current.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    };

    toggleMediaViewDiv = (e) => {
        if (e) e.stopPropagation();

        this.setState(prev => ({showMediaViewDiv: !prev.showMediaViewDiv}));
    };

    render() {
        let {
            messages,
            openImageViewer,
            openVideoViewer,
            user,
            messagesTotalNumber,
            conversation,
            templates,
            updateTemplates,
            company,
            loadingOlderMessages,
        } = this.props;
        const {sentMessages, newMessages, newMessageCount, isAtBottom} = this.state;

        const reversedMessages = [...(messages)].reverse();

        return (
            <div style={{height: '100%', display: 'flex', gap: '8px'}}>
                <div key={conversation.id + "_sd"}
                     className="messages-window"
                     ref={this.messageWindow}
                     onScroll={this.handleScroll}
                     style={{
                         position: 'relative',
                         overflowY: 'scroll',
                         paddingRight: '15px',
                         width: '100%'
                     }}
                >
                    {/*
                        The room the next page is fetched in.

                        It is 50px of nothing until the fetch starts, which is
                        what the scroll has to reach to ask for more; the
                        loader fades in on top of it rather than appearing in
                        its place, so the height never changes under the view.
                    */}
                    <div
                        className={'messages-older' + (loadingOlderMessages ? ' is-loading' : '')}
                        style={{display: messagesTotalNumber > messages.length ? 'flex' : 'none'}}
                    >
                        <LoadingComponent size={22}/>
                    </div>
                    {reversedMessages.map((message, index, arr) => {
                        let displayTimeLine = false;
                        if (index === 0) {
                            displayTimeLine = messagesTotalNumber <= messages.length;
                        } else {
                            const prevMessage = arr[index - 1];
                            displayTimeLine = !sameDay(convertFormBackendDate(message.date), convertFormBackendDate(prevMessage.date));
                        }

                        const isSent = message.sender === "self";
                        const isMessageSent = sentMessages.includes(message);
                        const isNewMessage = newMessages.includes(message);

                        if (!this.dateRefs[message.id]) {
                            this.dateRefs[message.id] = React.createRef();
                        }

                        const currentHighlightedId = this.props.searchResults[this.props.currentSearchIndex] ? this.props.searchResults[this.props.currentSearchIndex].id : '';
                        const isHighlighted = message.id === currentHighlightedId;

                        return (
                            <div key={message.id + "_sd"} ref={this.dateRefs[message.id]}
                            >
                                <Message
                                    readOnly={this.props.readOnly}
                                    childRef={this.props.childRef} // MessageInput ref
                                    setReplyTo={this.props.setReplyTo}
                                    setEditingMessage={this.props.setEditingMessage}
                                    editingMessage={this.props.editingMessage}
                                    replyTo={this.props.replyTo}
                                    company={company}
                                    templates={templates}
                                    updateTemplates={updateTemplates}
                                    openOperatorProfile={this.props.openOperatorProfile}
                                    conversation={conversation}
                                    key={message.id + "_"}
                                    message={message}
                                    openImageViewer={openImageViewer}
                                    openVideoViewer={openVideoViewer}
                                    displayTimeLine={displayTimeLine}
                                    user={user}
                                    isSent={isSent}
                                    isMessageSent={isMessageSent}
                                    isNewMessage={isNewMessage}
                                    highlight={isHighlighted}
                                    messageConfig={this.props.messageConfig}
                                />
                            </div>
                        );
                    })}
                    {reversedMessages && reversedMessages.length === 0 && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            flexDirection: 'column',
                            gap: '10px',
                            color: 'var(--ds-neutral-400)'
                        }}>
                            <ForumIcon width={80} height={80}/>
                            <Translate text={'noMessages'}/>
                        </div>
                    )}

                    {/*
                        One button, not two.

                        It used to appear only when a message arrived while the
                        operator was reading further up - so scrolling back down
                        after looking something up meant dragging all the way, and
                        a long conversation is a long drag. It is here whenever the
                        foot of the conversation is off screen, and it carries the
                        count when there is one: the same button, saying more.
                    */}
                    <button
                        type="button"
                        className={'scroll-to-bottom'
                            + (newMessageCount > 0 ? ' has-new' : '')
                            + ((!isAtBottom || newMessageCount > 0) ? ' is-visible' : '')}
                        onClick={this.scrollToBottom}
                        tabIndex={(!isAtBottom || newMessageCount > 0) ? 0 : -1}
                        aria-hidden={(!isAtBottom || newMessageCount > 0) ? 'false' : 'true'}
                    >
                        <ChevronDownIcon width={18} height={18} stroke="currentColor" strokeWidth={2}/>
                        {newMessageCount > 0 && (
                            /* keyed by the count: a new number is a new element,
                               which is what gives it its tick */
                            <span className="scroll-to-bottom__count" key={newMessageCount}>
                                {newMessageCount}
                            </span>
                        )}
                    </button>

                    <TypingIndicator messageWindow={this.messageWindow}
                                     conversation={conversation}
                                     conversationId={conversation.id}/>

                    <div ref={this.bottomDiv}/>

                </div>

            </div>
        );
    }
}

MessageList.propTypes = {
    messages: PropTypes.array.isRequired,
    user: PropTypes.object.isRequired,
    openImageViewer: PropTypes.func.isRequired,
    openVideoViewer: PropTypes.func.isRequired,
    messagesTotalNumber: PropTypes.number.isRequired,
    loadOlderMessages: PropTypes.func.isRequired,
    loadingOlderMessages: PropTypes.bool.isRequired,
};

export default MessageList;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/chat/chat-components/MessageList.jsx